import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useConfirm } from '../ui/ConfirmDialog'
import { useNavGuard } from './NavGuard'

/**
 * Unsaved-changes guard shared by both designers.
 *
 * While `dirty` is true:
 * - a `beforeunload` listener blocks refresh/close with the native prompt
 * - a nav guard (consulted by LeftNav links) opens the ConfirmDialog
 *   ("Leave without saving?") before navigating away
 *
 * While `dirty` is false neither is registered, so clean navigation is
 * completely unaffected.
 */
export function useUnsavedGuard(dirty: boolean): void {
  const confirm = useConfirm()
  const { t } = useTranslation()

  // beforeunload: only register while dirty so clean navigations are unaffected.
  useEffect(() => {
    if (!dirty) return
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault()
      // Legacy browser support
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  // Nav guard: prompt when leaving via LeftNav while dirty.
  const navGuardFn = useMemo(
    () =>
      dirty
        ? () =>
            confirm({
              title: t('confirm.leaveUnsaved.title'),
              body: t('confirm.leaveUnsaved.body'),
              confirmLabel: t('common.leave'),
              cancelLabel: t('common.stay'),
              danger: true,
            })
        : null,
    // confirm is stable (useCallback from ConfirmProvider)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dirty, t],
  )
  useNavGuard(navGuardFn)
}
